import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useMessage } from '@/hooks/use-message';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/use-language';
import { devLog } from '@/utils/console-utils';

export function useStartChat() {
  const { user } = useAuth();
  const { createDirectConversation, setActiveConversation } = useMessage();
  const { toast } = useToast();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const startChat = useCallback(async (participantId: string, participantName: string) => {
    devLog.log('useStartChat: Starting chat with:', { participantId, participantName, userExists: !!user });
    
    if (!participantId) {
      devLog.warn('useStartChat: No participant ID provided');
      toast({ 
        title: t('common.error'), 
        description: 'Cannot start chat: participant ID not found',
        variant: 'destructive'
      });
      return false;
    }
    if (!user || !user.id) {
      devLog.warn('useStartChat: No authenticated user or user ID');
      toast({ 
        title: t('common.error'), 
        description: 'You must be logged in to start a chat',
        variant: 'destructive'
      });
      return false;
    }
    
    if (user.id === participantId) {
      devLog.warn('useStartChat: Attempted to start chat with self');
      toast({
        title: t('common.error'),
        description: 'Cannot start chat with yourself',
        variant: 'destructive'
      });
      return false;
    }
    
    try {
      const convId = await createDirectConversation(participantId, participantName);
      devLog.log('useStartChat: Conversation created with ID:', convId);
      
      if (convId) {
        setActiveConversation(convId);
        navigate('/messages');
        toast({
          title: t('messages.chat_started') || 'Chat started',
          description: `Started chat with ${participantName}`
        });
        return true;
      } else {
        throw new Error('Failed to create conversation - no conversation ID returned');
      }
    } catch (error) {
      devLog.error('useStartChat: Error starting chat:', error);
      toast({ 
        title: t('common.error'), 
        description: `Failed to start chat with ${participantName}. Please try again.`,
        variant: 'destructive'
      });
      return false;
    }
  }, [user, createDirectConversation, setActiveConversation, navigate, toast, t]);

  return { startChat };
}
